import { isNil } from 'lodash';
import type { AnalysisView, AnalysisViewTypeEnum, Fund, Portfolio, PrivateFund, PrivatePortfolioNode } from 'venn-api';
import type { StudioRequestSubject, SubjectWithOptionalFee } from 'venn-state';
import type AnalysisSubject from '../analysis/AnalysisSubject';
import type {
  BlockSubjectType,
  CustomBlockTypeEnum,
  CustomizableBlockSetting,
  CustomizableMetric,
} from '../blocksettings';
import { selectStrategy } from '../portfolio';
import {
  PRIVATES_ASSET_GROWTH_BLOCKS,
  supportsOnlyPortfolioSubjects,
  supportsOnlyPrivateSubjects,
} from './blockTypeBehavior';
import { getRandomId } from './getRandomId';

export const isViewTemplate = (type?: AnalysisViewTypeEnum) =>
  type === 'REPORT_TEMPLATE' || type === 'TEARSHEET_TEMPLATE';

export const toggleStudioTemplateType = (type?: AnalysisViewTypeEnum): AnalysisViewTypeEnum => {
  if (!type) {
    return 'TEARSHEET';
  }
  if (type.includes('_TEMPLATE')) {
    return type.replace('_TEMPLATE', '') as AnalysisViewTypeEnum;
  }
  return `${type}_TEMPLATE` as AnalysisViewTypeEnum;
};

export const getNonTemplateType = (type?: AnalysisViewTypeEnum) =>
  (type?.replace('_TEMPLATE', '') ?? 'TEARSHEET') as AnalysisViewTypeEnum;
export const getTemplateType = (type?: AnalysisViewTypeEnum) =>
  `${getNonTemplateType(type)}_TEMPLATE` as AnalysisViewTypeEnum;

export const convertTemplateToView = (template: AnalysisView) => {
  return {
    ...template,
    id: undefined,
    name: undefined,
    analysisViewType: getNonTemplateType(template.analysisViewType),
    published: false,
    // Ensure that the new view does not have a owner context assigned
    ownerContextId: undefined,
  };
};

export const areCumulativeReturnMetrics = (selectedMetrics: string[] = []) =>
  selectedMetrics.length ? selectedMetrics.every((metric) => metric === 'cumulativeReturn') : false;

export const getSecondaryStrategyForStudioRequestSubject = (analysisSubject: AnalysisSubject): Portfolio | undefined =>
  isNil(analysisSubject.secondaryStrategy) ||
  analysisSubject.secondaryStrategy.id === analysisSubject.secondaryPortfolio?.id
    ? undefined
    : analysisSubject.secondaryStrategy;

export const getStrategyForStudioRequestSubject = (analysisSubject: AnalysisSubject): Portfolio | undefined =>
  isNil(analysisSubject.strategyId) ||
  isNil(analysisSubject.portfolio) ||
  analysisSubject.portfolio.id === analysisSubject.strategy?.id
    ? undefined
    : (selectStrategy(analysisSubject.strategyId, analysisSubject.portfolio) ?? undefined);

/**
 * Always returns a StudioRequestSubject without `modifiedPortfolio`, since that info can't be derived from AnalysisSubject.
 */
export const getRequestSubjectFromAnalysisSubject = (
  analysisSubject: AnalysisSubject,
  feesMapping?: { [key: string]: number },
): StudioRequestSubject & { modifiedPortfolio: undefined } => {
  const proxySetId = analysisSubject.proxySetId;
  return {
    id: String(analysisSubject.id),
    name: analysisSubject.name,
    ...(analysisSubject.superType === 'private-investment'
      ? {
          privateFund: analysisSubject.superItem as PrivateFund,
          subjectType: 'INVESTMENT',
          private: true,
        }
      : analysisSubject.superType === 'private-portfolio'
        ? {
            privatePortfolio: analysisSubject.superItem as PrivatePortfolioNode,
            subjectType: 'PORTFOLIO',
            private: true,
          }
        : analysisSubject.superType === 'investment'
          ? {
              fund: analysisSubject.superItem as Fund,
              subjectType: 'INVESTMENT',
              private: false,
            }
          : {
              // TODO: VENN-21001: fix case where we have an instrument-only portfolio (usually as individual benchmark)
              portfolio: (analysisSubject.getOptionsCopy().instrumentIdOnly
                ? {
                    id: analysisSubject.id,
                    name: analysisSubject.name,
                    // @ts-expect-error: using private field to workaround instrument-only portfolio
                    historical: analysisSubject._portfolio?.historical,
                  }
                : analysisSubject.superItem) as Portfolio,
              strategy: getStrategyForStudioRequestSubject(analysisSubject),
              subjectType: 'PORTFOLIO',
              private: false,
            }),
    individualBenchmark: analysisSubject.activeBenchmark,
    portfolioComparisonType: undefined,
    primaryPortfolio: undefined,
    proxySetId,
    feesMapping: analysisSubject.isHistoricalPortfolio ? undefined : feesMapping,
    modifiedPortfolio: undefined,
    modifiedPrivatePortfolio: undefined,
  };
};

export const getRequestSubjectForSecondaryAnalysisSubject = (
  analysisSubject: AnalysisSubject,
): StudioRequestSubject | undefined => {
  const primary = analysisSubject.portfolio;
  const secondary = analysisSubject.secondaryPortfolio;
  const portfolioComparisonType =
    analysisSubject.secondaryPortfolioComparisonType === 'NONE'
      ? undefined
      : analysisSubject.secondaryPortfolioComparisonType;

  if (isNil(primary) || isNil(secondary) || isNil(portfolioComparisonType)) {
    return undefined;
  }

  // A sub-strategy selected on AnalysisSubject has no corresponding sub-strategy in secondary portfolio
  if (analysisSubject.strategy?.id !== primary.id && isNil(analysisSubject.secondaryStrategy)) {
    return undefined;
  }

  if (analysisSubject.private) {
    return undefined;
  }

  return {
    id: String(secondary.id),
    name: secondary.name,
    subjectType: 'PORTFOLIO',
    portfolio: secondary,
    strategy: getSecondaryStrategyForStudioRequestSubject(analysisSubject),
    modifiedPortfolio: secondary,
    individualBenchmark: secondary.compare?.find((benchmark) => benchmark.primary),
    portfolioComparisonType,
    primaryPortfolio: primary,
    private: false,
    proxySetId: undefined,
  };
};

export const getSubjectFromRequestSubject = (requestSubject: StudioRequestSubject): SubjectWithOptionalFee =>
  getSubjectFromStudioRequestSubject(requestSubject, undefined);

/**
 * Gets the analyzed subject from the request subject
 * eg, if a sleeve is selected, the subject of the sleeve rather than the root portfolio
 * @param requestSubject the given request subject
 */
export const getAnalyzedSubjectFromRequestSubject = (requestSubject: StudioRequestSubject): SubjectWithOptionalFee => {
  const strategy = requestSubject.strategy;
  return getSubjectFromStudioRequestSubject(requestSubject, strategy);
};

const getSubjectFromStudioRequestSubject = (
  requestSubject: StudioRequestSubject,
  strategy: Portfolio | undefined,
): SubjectWithOptionalFee => {
  const portfolioNode = strategy ?? requestSubject.portfolio;
  const feesMapping = portfolioNode?.historical ? undefined : requestSubject.feesMapping;

  if (requestSubject.subjectType === 'INVESTMENT') {
    return requestSubject.private
      ? { type: 'private-fund', privateFundId: requestSubject.id, feesMapping }
      : { type: 'fund', fundId: requestSubject.id, feesMapping };
  }

  if (requestSubject.private) {
    return { type: 'private-portfolio', privatePortfolioId: requestSubject.id, feesMapping };
  }

  const portfolioId =
    strategy && !strategy?.fund && strategy?.id
      ? Number(strategy.id)
      : portfolioNode?.id
        ? Number(portfolioNode.id)
        : undefined;
  const portfolioVersion =
    requestSubject.portfolioComparisonType === 'SAVED' ? requestSubject.portfolio?.version : undefined;

  return { type: 'portfolio', portfolioId: portfolioId ?? NaN, portfolioVersion, feesMapping };
};

export const getBlockSubjectTypeName = (block: CustomizableBlockSetting) => {
  let prefix = '';
  const supportedSubjectTypes = block.supportedSubjectTypes;
  if (supportsOnlyPrivateSubjects(supportedSubjectTypes)) {
    prefix = 'private ';
  }
  return `${prefix}${supportsOnlyPortfolioSubjects(supportedSubjectTypes) ? 'portfolio' : 'subject'}`;
};

export const isReport = (analysisView: AnalysisView | AnalysisViewTypeEnum) => {
  if (typeof analysisView === 'string') {
    return analysisView === 'REPORT' || analysisView === 'REPORT_TEMPLATE';
  }
  return (
    analysisView?.analysisViewType === 'REPORT' ||
    analysisView?.analysisViewType === 'REPORT_TEMPLATE' ||
    analysisView?.customViewOptions?.reportingStyle === 'REPORT'
  );
};

export const makeCustomMetric = (name: string): CustomizableMetric => ({
  key: getRandomId(),
  dataType: 'TEXT',
  label: name,
  analysisType: 'CUSTOM_ENTRY',
});

/**
 * returns as-of-date for historical blocks that require it
 */
export const PRO_FORMA_HISTORICAL_SIMULATION_DATE_BLOCKS: CustomBlockTypeEnum[] = [
  'GROWTH_SIMULATION',
  ...PRIVATES_ASSET_GROWTH_BLOCKS,
];
export const PRO_FORMA_HISTORICAL_AS_OF_DATE_PARAMETERIZED_BLOCKS: CustomBlockTypeEnum[] = [
  'PORTFOLIO_BREAKDOWN',
  'PORTFOLIO_COMPARISON',
];

export const PRO_FORMA_HISTORICAL_BLOCKS: CustomBlockTypeEnum[] = [
  ...PRO_FORMA_HISTORICAL_AS_OF_DATE_PARAMETERIZED_BLOCKS,
  ...PRO_FORMA_HISTORICAL_SIMULATION_DATE_BLOCKS,
  'SCENARIO',
];

export const HISTORICAL_BENCHMARK_BLOCKS: CustomBlockTypeEnum[] = ['CORRELATION'];

export const CORRELATION_BLOCK_PRO_FORMA_METRICS = [
  'portfolioCorrelation',
  'residualPortfolioCorrelation',
  'portfolioDownsideCorrelation',
];

export const PERFORMANCE_SUMMARY_BLOCK_PRO_FORMA_METRICS = [
  'FORECAST-annualizedReturn',
  'FORECAST-sharpe',
  'FORECAST-volatility',
  'RESIDUAL_FORECAST-annualizedReturn',
  'RESIDUAL_FORECAST-volatility',
  'RESIDUAL_FORECAST-sharpe',
  'grossExposure',
  'netExposure',
  'grossLeverageRatio',
  'netLeverageRatio',
];

export const PRO_FORMA_HISTORICAL_METRICS = [
  ...CORRELATION_BLOCK_PRO_FORMA_METRICS,
  ...PERFORMANCE_SUMMARY_BLOCK_PRO_FORMA_METRICS,
];

export const getBlockSubjectType = (subject: StudioRequestSubject): BlockSubjectType => {
  if (subject.subjectType === 'INVESTMENT' && !subject.private) {
    return 'INVESTMENT';
  }
  if (subject.subjectType === 'INVESTMENT' && subject.private) {
    return 'PRIVATE_INVESTMENT';
  }
  if (subject.subjectType === 'PORTFOLIO' && subject.private) {
    return 'PRIVATE_PORTFOLIO';
  }
  if (isHistoricalSubject(subject)) {
    return 'HISTORICAL_PORTFOLIO';
  }
  return 'PROFORMA_PORTFOLIO';
};

export const isHistoricalSubject = (subject?: StudioRequestSubject) =>
  (subject?.modifiedPortfolio ?? subject?.portfolio)?.historical ?? false;

/**
 * Returns true if the block supports explicit as of date selection input in block settings
 */
export const isExplicitAsOfDateSelectionEnabled = (
  subjects: StudioRequestSubject[],
  customBlockType: CustomBlockTypeEnum,
): boolean => {
  if (!PRO_FORMA_HISTORICAL_AS_OF_DATE_PARAMETERIZED_BLOCKS.includes(customBlockType)) {
    return false;
  }
  return subjects.some(isHistoricalSubject);
};
